import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';
import type { Configuration, IRequest } from '../..';
import HttpMethod from '../../request/HttpMethod';
import Requester from '../../request/Requester';
import TimeZone from '../../request/Timezone';
import type {
  DriverAggregationTripsAndStopsRequest,
  DriverDailyAggregationTripsAndStopsRequest,
  EventsTripsAndStopsRequest,
  TripsAndStopsEmailReportRequest,
  TripsAndStopsReportRequest,
  TripsAndStopsResponse,
  VehicleAggregationTripsAndStopsRequest,
  VehicleDailyAggregationTripsAndStopsRequest,
} from './Types';

export class TripsAndStopsRequester {
  private basePath = '/analytics/v1/trips-and-stops';

  private requester: Requester;

  constructor(configuration: Configuration) {
    this.requester = new Requester(configuration);
  }

  public getVehicleAggregation = async (params: VehicleAggregationTripsAndStopsRequest): Promise<TripsAndStopsResponse> => {
    const requestData: IRequest = {
      endpoint: `${this.basePath}/vehicle`,
      method: HttpMethod.POST,
      body: JSON.stringify(snakecaseKeys({ ...params, timezone: TimeZone.getTimezone() })),
    };

    const response = await this.requester.request(requestData);
    return camelcaseKeys(response.data.data, {
      deep: true,
    });
  };

  public getDriverAggregation = async (params: DriverAggregationTripsAndStopsRequest): Promise<TripsAndStopsResponse> => {
    const requestData: IRequest = {
      endpoint: `${this.basePath}/driver`,
      method: HttpMethod.POST,
      body: JSON.stringify(snakecaseKeys({ ...params, timezone: TimeZone.getTimezone() })),
    };

    const response = await this.requester.request(requestData);
    return camelcaseKeys(response.data.data, {
      deep: true,
    });
  };

  public getDailyVehicleAggregation = async (params: VehicleDailyAggregationTripsAndStopsRequest): Promise<TripsAndStopsResponse> => {
    const requestData: IRequest = {
      endpoint: `${this.basePath}/vehicle/daily`,
      method: HttpMethod.POST,
      body: JSON.stringify(snakecaseKeys({ ...params, timezone: TimeZone.getTimezone() })),
    };

    const response = await this.requester.request(requestData);
    return camelcaseKeys(response.data.data, {
      deep: true,
    });
  };

  public getDailyDriverAggregation = async (params: DriverDailyAggregationTripsAndStopsRequest): Promise<TripsAndStopsResponse> => {
    const requestData: IRequest = {
      endpoint: `${this.basePath}/driver/daily`,
      method: HttpMethod.POST,
      body: JSON.stringify(snakecaseKeys({ ...params, timezone: TimeZone.getTimezone() })),
    };

    const response = await this.requester.request(requestData);
    return camelcaseKeys(response.data.data, {
      deep: true,
    });
  };

  public getTripsAndStopsEvents = async (params: EventsTripsAndStopsRequest): Promise<TripsAndStopsResponse> => {
    const requestData: IRequest = {
      endpoint: `${this.basePath}`,
      method: HttpMethod.POST,
      body: JSON.stringify(snakecaseKeys({ ...params, timezone: TimeZone.getTimezone() }, { deep: true })),
    };

    const response = await this.requester.request(requestData);
    return camelcaseKeys(response.data.data, {
      deep: true,
    });
  };

  public downloadTripsAndStopsReport = async (request: TripsAndStopsReportRequest): Promise<Blob> => {
    const requestBody = {
      timezone: TimeZone.getTimezone(),
      ...snakecaseKeys(request),
    };
    const requestData: IRequest = {
      endpoint: `${this.basePath}/report`,
      method: HttpMethod.POST,
      body: JSON.stringify(snakecaseKeys(requestBody)),
      responseType: 'arraybuffer',
    };

    const response = await this.requester.request(requestData);
    return new Blob([response.data]);
  };

  public requestTripsAndStopsReport = async (request: TripsAndStopsEmailReportRequest): Promise<string> => {
    const requestBody = {
      timezone: TimeZone.getTimezone(),
      ...snakecaseKeys(request),
    };
    const requestData: IRequest = {
      endpoint: `${this.basePath}/report/mail`,
      method: HttpMethod.POST,
      body: JSON.stringify(snakecaseKeys(requestBody)),
    };

    const response = await this.requester.request(requestData);
    return response.data;
  };
}

export default TripsAndStopsRequester;
